import { bearingDeg, distanceNm } from './geo.js';

/**
 * Normalize an angle difference to the range (-180, +180].
 * Positive = clockwise turn, negative = counter-clockwise.
 */
export function normalizeTurnAngle(angle) {
  let a = angle % 360;
  if (a > 180) a -= 360;
  if (a <= -180) a += 360;
  return a;
}

/**
 * Ambiguity threshold in degrees.
 * Turn angles within this of 0 or 180 are flagged as ambiguous.
 */
const AMBIGUITY_THRESHOLD = 10;

/**
 * Infer port/starboard rounding for each mark in a sequence.
 *
 * For mark B in sequence A → B → C:
 * - Compute turn angle = normalize(bearing(B→C) - bearing(A→B))
 * - Positive (clockwise / turning right): round to starboard (mark on right)
 * - Negative (counter-clockwise / turning left): round to port (mark on left)
 * - Near 0° or ±180°: ambiguous
 *
 * First and last marks have no turn angle — default to port, flagged for review.
 *
 * @param {string[]} markIds - ordered mark IDs
 * @param {Object} markLookup - { markId: [lat, lng] }
 * @returns {{ markId: string, rounding: 'port'|'starboard'|'ambiguous', turnAngle: number|null, confident: boolean }[]}
 */
export function inferRoundings(markIds, markLookup) {
  if (!markIds || markIds.length === 0) return [];

  return markIds.map((markId, i) => {
    // First mark — no incoming leg
    if (i === 0) {
      return { markId, rounding: 'port', turnAngle: null, confident: false };
    }
    // Last mark — no outgoing leg
    if (i === markIds.length - 1) {
      return { markId, rounding: 'port', turnAngle: null, confident: false };
    }

    const posA = markLookup[markIds[i - 1]];
    const posB = markLookup[markId];
    const posC = markLookup[markIds[i + 1]];

    // Missing position data — can't compute
    if (!posA || !posB || !posC) {
      return { markId, rounding: 'port', turnAngle: null, confident: false };
    }

    const inBearing = bearingDeg(posA[0], posA[1], posB[0], posB[1]);
    const outBearing = bearingDeg(posB[0], posB[1], posC[0], posC[1]);
    const turnAngle = normalizeTurnAngle(outBearing - inBearing);
    const absTurn = Math.abs(turnAngle);

    // Ambiguous if near 0° (straight) or ±180° (reversal)
    if (absTurn < AMBIGUITY_THRESHOLD || absTurn > 180 - AMBIGUITY_THRESHOLD) {
      return { markId, rounding: 'ambiguous', turnAngle, confident: false };
    }

    // Positive turn (clockwise / turning right) = starboard rounding (mark to right)
    // Negative turn (counter-clockwise / turning left) = port rounding (mark to left)
    const rounding = turnAngle > 0 ? 'starboard' : 'port';
    return { markId, rounding, turnAngle, confident: true };
  });
}

/**
 * Calculate total distance for a mark sequence using mark positions.
 * @param {string[]} markIds
 * @param {Object} markLookup - { markId: [lat, lng] }
 * @returns {{ totalNm: number|null, legs: { from: string, to: string, distNm: number|null }[] }}
 */
export function calculateDistance(markIds, markLookup) {
  if (!markIds || markIds.length < 2) return { totalNm: null, legs: [] };

  const legs = [];
  let total = 0;
  let complete = true;

  for (let i = 0; i < markIds.length - 1; i++) {
    const posA = markLookup[markIds[i]];
    const posB = markLookup[markIds[i + 1]];
    if (!posA || !posB) {
      legs.push({ from: markIds[i], to: markIds[i + 1], distNm: null });
      complete = false;
      continue;
    }
    const d = distanceNm(posA[0], posA[1], posB[0], posB[1]);
    legs.push({ from: markIds[i], to: markIds[i + 1], distNm: d });
    total += d;
  }

  return { totalNm: complete ? total : null, legs };
}

/**
 * Calculate distance contribution of a start or finish line to the adjacent course mark.
 * @param {{ type: string, offsetNm: number, markA: string, markB: string }|null} lineConfig
 * @param {string|undefined} adjacentMarkId - first mark (for start) or last mark (for finish)
 * @param {Object} markLookup - { markId: [lat, lng] }
 * @returns {number|null} leg distance in nm, or null if incomplete
 */
function resolveLineLegDistance(lineConfig, adjacentMarkId, markLookup) {
  if (!lineConfig || !adjacentMarkId) return null;

  if (lineConfig.type === 'offset' || lineConfig.type === 'vessel') {
    return lineConfig.offsetNm ?? null;
  }

  if (lineConfig.type === 'fixed') {
    const posA = markLookup[lineConfig.markA];
    const posB = markLookup[lineConfig.markB];
    const posAdj = markLookup[adjacentMarkId];
    if (!posA || !posB || !posAdj) return null;

    // Distance from midpoint of the line to the adjacent course mark
    const midLat = (posA[0] + posB[0]) / 2;
    const midLng = (posA[1] + posB[1]) / 2;
    return distanceNm(midLat, midLng, posAdj[0], posAdj[1]);
  }

  if (lineConfig.type === 'fixed_mark') {
    // Line sits ON markA (offsetNm defaults 0); the leg to the adjacent course
    // mark is just markA → adjacent. When markA IS the adjacent course mark
    // (start/finish AT that mark) the leg is 0. (ra-gbrd.22)
    const posA = markLookup[lineConfig.markA];
    const posAdj = markLookup[adjacentMarkId];
    if (!posA || !posAdj) return null;
    return distanceNm(posA[0], posA[1], posAdj[0], posAdj[1]);
  }

  return null;
}

/**
 * Calculate full course distance including start and finish leg distances.
 * Total = start leg + mark-to-mark legs + finish leg.
 *
 * @param {string[]} markIds - ordered mark IDs
 * @param {Object} markLookup - { markId: [lat, lng] }
 * @param {{ type: string, offsetNm: number, markA: string, markB: string }|null} startLine
 * @param {{ type: string, offsetNm: number, markA: string, markB: string }|null} finishLine
 * @returns {{ totalNm: number|null, legs: Array, startLegNm: number|null, finishLegNm: number|null }}
 */
export function calculateFullDistance(markIds, markLookup, startLine, finishLine) {
  const markResult = calculateDistance(markIds, markLookup);

  const startLegNm = resolveLineLegDistance(startLine, markIds?.[0], markLookup);
  const finishLegNm = resolveLineLegDistance(finishLine, markIds?.[markIds.length - 1], markLookup);

  const markComplete = markResult.totalNm !== null;
  const startComplete = startLegNm !== null;
  const finishComplete = finishLegNm !== null;

  const totalNm = (markComplete && startComplete && finishComplete)
    ? startLegNm + markResult.totalNm + finishLegNm
    : null;

  return {
    totalNm,
    legs: markResult.legs,
    startLegNm,
    finishLegNm,
  };
}

/**
 * Find fuzzy matches for an unknown mark ID against known marks.
 * Returns marks sorted by similarity (best first).
 * @param {string} unknownId
 * @param {Array<{id: string, name: string}>} knownMarks
 * @param {number} maxResults
 * @returns {Array<{id: string, name: string, score: number}>}
 */
export function fuzzyMatchMark(unknownId, knownMarks, maxResults = 3) {
  if (!unknownId || !knownMarks || knownMarks.length === 0) return [];

  const needle = unknownId.toUpperCase();
  const scored = knownMarks.map((m) => {
    const id = (m.id || '').toUpperCase();
    const name = (m.name || '').toUpperCase();
    let score = 0;

    // Exact prefix match on id
    if (id.startsWith(needle)) score += 3;
    else if (needle.startsWith(id)) score += 2;
    // Single character distance
    else if (id.length === 1 && needle.length === 1) {
      score += Math.abs(id.charCodeAt(0) - needle.charCodeAt(0)) <= 2 ? 1 : 0;
    }

    // Name contains the search
    if (name.includes(needle)) score += 1;

    return { id: m.id, name: m.name, score };
  });

  return scored
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, maxResults);
}
